import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  ClerkLoading,
  ClerkLoaded,
} from '@clerk/clerk-react';
import { useConvexAuth } from 'convex/react';
import { MoneyLayout } from './components/MoneyLayout';
import { UpcomingPage, SpendingPage } from './pages/Money';
import { LoginPage } from './pages/Login';
import { YnabCallback } from './pages/YnabCallback';
import { useProfile } from './hooks/useProfile';
import { useAutoConnectYnab } from './hooks/useYnab';

const FinanceAssistantPage = lazy(() => import('./pages/FinanceAssistant').then(module => ({ default: module.FinanceAssistantPage })));
const NetWorthPage = lazy(() => import('./pages/NetWorth').then(module => ({ default: module.NetWorthPage })));
const AgentStatusPage = lazy(() => import('./pages/AgentStatus').then(module => ({ default: module.AgentStatusPage })));
const TransactionsPage = lazy(() => import('./pages/Transactions').then(module => ({ default: module.TransactionsPage })));
const DebtsPage = lazy(() => import('./pages/Debts').then(module => ({ default: module.DebtsPage })));
const LoanCalendarPage = lazy(() => import('./pages/LoanCalendar').then(module => ({ default: module.LoanCalendarPage })));
const RunbookPage = lazy(() => import('./pages/Runbook').then(module => ({ default: module.RunbookPage })));
const BudgetPage = lazy(() => import('./pages/Budget').then(module => ({ default: module.BudgetPage })));
const InvestmentsPage = lazy(() => import('./pages/Investments').then(module => ({ default: module.InvestmentsPage })));
const StocksPage = lazy(() => import('./pages/Stocks').then(module => ({ default: module.StocksPage })));
const SubscriptionsPage = lazy(() => import('./pages/Subscriptions').then(module => ({ default: module.SubscriptionsPage })));
const ReviewPage = lazy(() => import('./pages/Review').then(module => ({ default: module.ReviewPage })));
const BattleRoomPage = lazy(() => import('./pages/BattleRoom').then(module => ({ default: module.BattleRoomPage })));
const RecommendationsPage = lazy(() => import('./pages/Recommendations').then(module => ({ default: module.RecommendationsPage })));
const GoalsPage = lazy(() => import('./pages/Goals').then(module => ({ default: module.GoalsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then(module => ({ default: module.SettingsPage })));

function LoadingScreen() {
  return (
    <div className="app-loading-screen">
      <div role="status" className="flex flex-col items-center gap-4">
        <div aria-hidden="true" className="h-10 w-10 motion-safe:animate-spin rounded-full border-2 border-accent border-t-transparent" />
        <p className="text-text-secondary">Opening your finances…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useConvexAuth();
  // Auto-create profile when user is authenticated
  const { isLoading: profileLoading } = useProfile();
  // Auto-connect YNAB if env token is available
  useAutoConnectYnab();

  if (isLoading || profileLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <RedirectToSignIn />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <ClerkLoading>
        <LoadingScreen />
      </ClerkLoading>

      <ClerkLoaded>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <>
                <SignedIn>
                  <Navigate to="/" replace />
                </SignedIn>
                <SignedOut>
                  <LoginPage />
                </SignedOut>
              </>
            }
          />

          {/* YNAB OAuth callback */}
          <Route path="/ynab/callback" element={<YnabCallback />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MoneyLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<UpcomingPage />} />
            <Route path="spending" element={<SpendingPage />} />
            <Route path="agent" element={<AgentStatusPage />} />
            <Route path="assistant" element={<FinanceAssistantPage />} />
            <Route path="net-worth" element={<NetWorthPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="debts" element={<DebtsPage />} />
            <Route path="debts/calendar" element={<LoanCalendarPage />} />
            <Route path="debts/runbook" element={<RunbookPage />} />
            <Route path="budget" element={<BudgetPage />} />
            <Route path="investments" element={<InvestmentsPage />} />
            <Route path="stocks" element={<StocksPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="review" element={<ReviewPage />} />
            <Route path="battle" element={<BattleRoomPage />} />
            <Route path="recommendations" element={<RecommendationsPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ClerkLoaded>
    </>
  );
}
